import { UserFilterType } from '@/entities/user';
import requestApi, { HTTP_METHOD } from '@/utils/axios.http';
import { TOKEN_API_PUBLIC } from '@/utils/constant.utils';
import { TUserSchema } from '@/schemas/user/user.schema';
import { mapUserFilterAPI } from '@/utils/map.utils';
import { LatestCreatedUser, ListAllClient, TotalActiveUser } from '../entities/user';

const USER_URI = '/user';

export const createNewUser = async (user: TUserSchema) => {
  return requestApi({
    url: USER_URI,
    method: HTTP_METHOD.POST,
    body: user,
    headers: {
      'Content-Type': 'application/json',
      'x-api-token': TOKEN_API_PUBLIC,
    },
  });
};

export const getExistingUser = async (query?: TUserSchema) => {
  let url = `${USER_URI}/existing`;
  const params = new URLSearchParams();

  if (query?.id) {
    params.append('id', query.id);
  }

  if (query?.email) {
    params.append('email', query.email);
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  return requestApi({
    url,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
      'x-api-token': TOKEN_API_PUBLIC,
    },
  });
};

export const getAllUsers = async (
  filter: UserFilterType,
  page: number,
  limit: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc'
) => {
  let url = `${USER_URI}/all?${mapUserFilterAPI(
    filter
  )}&page=${page}&limit=${limit}`;

  // Add sort parameters if provided
  if (sortBy) {
    url += `&sortBy=${sortBy}&sortDirection=${sortDirection || 'asc'}`;
  }

  return requestApi({
    url,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getTotalActiveUsers = async (query?: TotalActiveUser) => {
  return requestApi({
    url: `${USER_URI}/totalActive${query?.id ? `?id=${query?.id}` : ''}`,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const listAllClients = async (query?: ListAllClient) => {
  let url = `${USER_URI}/all/clients`;
  const params = new URLSearchParams();

  if (query?.userId) {
    params.append('userId', query.userId);
  }

  if (query?.groupId) {
    params.append('groupId', query?.groupId);
  }

  if (query?.companyId) {
    params.append('companyId', query?.companyId);
  }
  
  if (query?.branchId) {
    params.append('branchId', query?.branchId);
  }

  if (query?.type) {
    params.append('type', query?.type);
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  return requestApi({
    url,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getUserById = async (id: string) => {
  return requestApi({
    url: `${USER_URI}/${id}`,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getAssignedAdmin = async (id: string) => {
  return requestApi({
    url: `${USER_URI}/assigned/admin/${id}`,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getAssignedModerator = async (id: string) => {
  return requestApi({
    url: `${USER_URI}/assigned/moderator/${id}`,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getCreatedUsersPerMonth = async (userId?: string) => {
  return requestApi({
    url: `${USER_URI}/stats/createdPerMonth${
      userId ? `?userId=${userId}` : ''
    }`,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getLatestCreatedUsers = async (query?: LatestCreatedUser) => {
  let url = `${USER_URI}/latestCreated`;

  if (query?.id) {
    url += `/${query?.id}`;
  }

  if (query?.numberOfItems) {
    url += `/${query?.numberOfItems}`;
  }

  return requestApi({
    url,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const updateUser = async (user: TUserSchema) => {
  return requestApi({
    url: `${USER_URI}/update`,
    method: HTTP_METHOD.PATCH,
    body: user,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const updateManyUsers = async (users: TUserSchema[]) => {
  return requestApi({
    url: `${USER_URI}/updateMany`,
    method: HTTP_METHOD.PATCH,
    body: users,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const updatePassword = async (id: string, password: string) => {
  return requestApi({
    url: `${USER_URI}/${id}/update-password/${password}`,
    method: HTTP_METHOD.PATCH,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};
